/**
 * What the current user may do on a project, from GET /projects/:id/team/me.
 *
 * The API enforces every one of these on its own; this only decides which
 * controls to offer. It fails closed: until the capabilities have loaded —
 * or when they could not be loaded — every action is unavailable, so a
 * privileged control never flashes on screen for someone who lacks it.
 * Viewing is not gated here: a page the user could open is already one the
 * API let them read.
 */
import { useProjectTeamMe, type ProjectCapabilities } from './projectTeam';

/** An action the API checks with a named capability (projectteam.KnownCapabilities). */
export type ProjectAction =
  | 'comment'
  | 'execute'
  | 'sign_off'
  | 'deploy'
  | 'delete_project'
  | 'approve'
  | 'edit_policies'
  | 'manage_team';

export type PermissionStatus = 'loading' | 'ready' | 'unavailable';

export interface ProjectPermissions {
  status: PermissionStatus;
  can: (action: ProjectAction) => boolean;
  /** Why the action is unavailable, for display; null when it is available. */
  reason: (action: ProjectAction) => string | null;
  /** The workspace role that reaches this project, when access comes through a workspace. */
  workspaceRole?: ProjectCapabilities['workspace_role'];
}

const FIELD: Record<ProjectAction, keyof ProjectCapabilities> = {
  comment: 'can_comment',
  execute: 'can_execute',
  sign_off: 'can_sign_off',
  deploy: 'can_deploy',
  delete_project: 'can_delete_project',
  approve: 'can_approve',
  edit_policies: 'can_edit_policies',
  manage_team: 'can_manage_team',
};

/** Mirrors projectteam.Service.RequireCapability: a project in bootstrap is open. */
export function capabilityHeld(caps: ProjectCapabilities | undefined, action: ProjectAction): boolean {
  if (!caps) return false;
  return caps[FIELD[action]] === true || caps.bootstrap_open === true;
}

function deniedReason(caps: ProjectCapabilities, action: ProjectAction): string {
  const ws = caps.workspace_role;
  // A project role stronger than the workspace one decides; only name the
  // workspace role when it is what the user's access rests on.
  const fromWorkspace = !!ws && caps.role !== 'admin' && caps.role !== 'lead' && caps.role !== 'member';
  if (fromWorkspace && ws === 'viewer') return 'Your Viewer role in this workspace is read-only.';
  if (fromWorkspace && ws === 'commentator') {
    return 'Your Commentator role in this workspace can view and comment, but not run or change work.';
  }
  if (fromWorkspace && ws === 'editor') {
    return 'Editors can run and prepare work. Approval, sign-off, deployment, deletion, policies and team management need a project lead, admin or owner.';
  }
  if (caps.role === 'viewer') return 'Your Viewer role on this project is read-only.';
  if (action === 'approve') return 'Approving needs a project lead, admin or owner.';
  if (action === 'edit_policies' || action === 'manage_team') return 'Only a project admin or owner can do this.';
  return 'Your role on this project does not allow this action.';
}

export function permissionsFrom(caps: ProjectCapabilities | undefined, status: PermissionStatus): ProjectPermissions {
  const ready = status === 'ready' && !!caps;
  const can = (action: ProjectAction) => ready && capabilityHeld(caps, action);
  const reason = (action: ProjectAction): string | null => {
    if (can(action)) return null;
    if (status === 'loading') return 'Checking your permissions…';
    if (!ready) return 'Your permissions could not be loaded, so this action is unavailable. Reload to try again.';
    return deniedReason(caps!, action);
  };
  return { status: ready ? 'ready' : status === 'loading' ? 'loading' : 'unavailable', can, reason, workspaceRole: caps?.workspace_role };
}

/** The current user's permissions on projectId; fails closed while loading or on error. */
export function useProjectPermissions(projectId: string): ProjectPermissions {
  const me = useProjectTeamMe(projectId);
  const status: PermissionStatus = me.isLoading || (me.isFetching && !me.data) ? 'loading' : me.data ? 'ready' : 'unavailable';
  return permissionsFrom(me.data?.capabilities, status);
}
